<script lang="ts">
    import InstagramTaggedProductQuery from "@apps/instagram/queries/InstagramTaggedProductQuery";
    import InstagramTaggedProductService from "@apps/instagram/services/InstagramTaggedProductService";
    import { hasNextPage } from "@apps/instagram/utils/paginationUtils";
    import InstagramBranding from "@apps/instagram/widgets/instagram_gallery_widget/components/InstagramBranding.svelte";
    import TikTokPostModal from "@apps/tiktok/components/TikTokPostModal/TikTokPostModal.svelte";
    import TikTokPostModalManager from "@apps/tiktok/components/TikTokPostModal/TikTokPostModalManager";
    import TikTokGalleryQuery from "@apps/tiktok/queries/TikTokGalleryQuery";
    import { TikTokPostsQuery } from "@apps/tiktok/queries/TikTokPostQuery";
    import TikTokGalleryService from "@apps/tiktok/services/TikTokGalleryService";
    import { TikTokPostsService } from "@apps/tiktok/services/TikTokPostsService";
    import { TikTokPostsStore } from "@apps/tiktok/stores/TikTokPostsStore";
    import { tiktokPostModalToken } from "@apps/tiktok/tokens";
    import { setTypedContext } from "@lib/svelte-typed-context";
    import { CartQuery } from "@modules/cart/queries/CartQuery";
    import { CartService } from "@modules/cart/services/CartService";
    import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
    import { lastValueFrom } from "rxjs";
    import { find, map, mergeMap } from "rxjs/operators";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import ErrorBanner from "@components/ErrorBanner.svelte";
    import LoadMore from "@components/LoadMore.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import ModalManagerProvider from "@components/Modal/ModalManager/ModalManagerProvider.svelte";

    import { tikTokGalleryWidgetContextToken } from "./context_tokens";
    import TikTokGalleryListingLayout from "./layouts/TikTokGalleryListingLayout.svelte";

    export let galleryId: number;
    export let tikTokGalleryQuery: TikTokGalleryQuery;
    export let tikTokGalleryService: TikTokGalleryService;
    export let tiktokPostsQuery: TikTokPostsQuery;
    export let tiktokPostsService: TikTokPostsService;
    export let tikTokPostsStore: TikTokPostsStore;
    export let taggedProductQuery: InstagramTaggedProductQuery;
    export let taggedProductService: InstagramTaggedProductService;
    export let cartService: CartService;
    export let cartQuery: CartQuery;
    export let designMode: boolean;
    export let locationController: LocationControllerInterface;
    export let productsService: ProductsServiceInterface;
    export let productsQuery: ProductsQuery;
    export let urlsBuilder: URLsBuilder;
    export let cartAvailabilityProvider: CartAvailabilityProvider;

    setTypedContext(tikTokGalleryWidgetContextToken, {
        cartService,
        cartQuery,
        tikTokGalleryService,
        tikTokGalleryQuery,
        tiktokPostsService,
        tiktokPostsQuery,
        taggedProductService,
        taggedProductQuery,
        productsQuery,
        productsService,
        locationController,
        cartAvailabilityProvider,
    });

    const i18nModule = container.resolve(I18nModule);
    const loadMoreT$ = i18nModule.exports.i18n.selectT(
        "tiktok.critical.TikTokGalleryWidget",
        "load_more"
    );
    const PER_PAGE = 8;
    const modalManager = new ModalManager();
    const gallery$ = tikTokGalleryQuery.gallery$;
    const getGalleryError$ = tikTokGalleryQuery.getGalleryError$;
    const widget$ = gallery$.pipe(
        mergeMap((v) => v?.displaySettings || []),
        find((_, idx) => {
            return idx === 0;
        })
    );
    const posts$ = tiktokPostsQuery.all$;
    const postsLoading$ = tiktokPostsQuery.loading$;
    const postsError$ = tiktokPostsQuery.error$;
    const paginationMeta$ = tiktokPostsQuery.paginationMeta$;
    const hasNextPage$ = paginationMeta$.pipe(
        map((paginationMeta) => {
            return hasNextPage(
                paginationMeta.perPage,
                paginationMeta.currentOffset,
                paginationMeta.totalCount
            );
        })
    );
    const postModalManager = new TikTokPostModalManager(
        tiktokPostsQuery,
        tikTokPostsStore,
        taggedProductService,
        productsService
    );
    onMount(async () => {
        lastValueFrom(tikTokGalleryService.getGalleryById(galleryId));
        lastValueFrom(widget$).then(() => {
            lastValueFrom(
                tiktokPostsService.getPostsByGalleryId({
                    galleryId,
                    perPage: PER_PAGE,
                    sortBy: $widget$?.sortBy,
                })
            );
        });
        const productsFetchingSubscription = tiktokPostsQuery.all$
            .pipe(
                map((posts) => {
                    posts.forEach((post) => {
                        post.tags.forEach((tag) => {
                            productsService.addProductForFetching(
                                tag.product.id
                            );
                        });
                    });
                })
            )
            .subscribe();
        return () => {
            productsFetchingSubscription.unsubscribe();
        };
    });
    const loadeMorePosts = () => {
        lastValueFrom(
            tiktokPostsService.loadMorePostsByGalleryId({
                galleryId: galleryId,
                perPage: PER_PAGE,
                offset: PER_PAGE + ($paginationMeta$.currentOffset | 0),
                sortBy: $widget$?.sortBy,
            })
        );
    };
</script>

<div class="gw-tt-gallery-widget">
    <ModalManagerProvider {modalManager}>
        <div class="gw-tt-gallery-widget__header">
            {#if $gallery$}
                <h2 class="gw-tt-gallery-widget__title">{$gallery$.title}</h2>
            {/if}
        </div>
        {#if $getGalleryError$ && designMode}
            <ErrorBanner>
                Error on getting gallery by id={galleryId}
            </ErrorBanner>
        {/if}
        {#if $gallery$}
            {#if !$gallery$.hideBranding}
                <InstagramBranding />
            {/if}
            {#if !$postsError$}
                <TikTokGalleryListingLayout
                    postModalToken={tiktokPostModalToken}
                    skeleton={$postsLoading$ && !$posts$.length}
                    posts={$posts$}
                />
                {#if $hasNextPage$}
                    <LoadMore
                        text={$loadMoreT$}
                        ariaLabel="load more button"
                        on:loadMore={loadeMorePosts}
                        loading={$postsLoading$}
                    />
                {/if}
                <TikTokPostModal
                    modalToken={tiktokPostModalToken}
                    tikTokPostModalManager={postModalManager}
                    {urlsBuilder}
                    hideBranding={$gallery$.hideBranding}
                    productQuickViewEnabled={$gallery$.allowInstantPurchase}
                />
            {/if}
        {/if}
    </ModalManagerProvider>
</div>

<style>
    .gw-tt-gallery-widget__header {
        padding-top: var(--gw-ig-gallery-widget-header-pt);
        padding-bottom: var(--gw-ig-gallery-widget-header-pb);
    }
    .gw-tt-gallery-widget__title {
        font-size: var(--gw-ig-gallery-widget-header-fs);
        text-align: center;
    }
</style>
